<template>
  <ContentWrap>
    <el-form inline>
      <el-form-item label="酒店">
        <el-select
          v-model="shopId"
          filterable
          class="!w-320px"
          placeholder="选择酒店"
          @change="loadRoomTypes"
        >
          <el-option
            v-for="item in hotels"
            :key="item.shopId"
            :label="`${item.shopName}（${operationModeLabel(item.operationMode)}）`"
            :value="item.shopId"
          />
        </el-select>
      </el-form-item>
      <el-button type="primary" :disabled="!shopId" @click="loadRoomTypes">查询</el-button>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      title="仅审核中房型可终审；通过后酒店仍需在 B 端主动上架，驳回或待审房型不会出现在 C 端。"
    />
    <el-empty v-if="!shopId" description="请先选择酒店" />
    <el-table v-else v-loading="loading" :data="roomTypes">
      <el-table-column type="expand">
        <template #default="{ row }">
          <el-table :data="row.skus || []" border class="sku-table">
            <el-table-column prop="ratePlanName" label="价型" />
            <el-table-column label="售价"
              ><template #default="scope">¥{{ money(scope.row.price) }}</template></el-table-column
            >
            <el-table-column prop="breakfastCount" label="早餐份数" />
            <el-table-column prop="maxGuests" label="入住人数" />
            <el-table-column label="入住晚数"
              ><template #default="scope"
                >{{ scope.row.minStay }} - {{ scope.row.maxStay }}</template
              ></el-table-column
            >
            <el-table-column prop="cancellationPolicy" label="取消规则" min-width="220" />
            <el-table-column label="状态"
              ><template #default="scope">{{
                scope.row.saleStatus === 1 ? '可售' : '停售'
              }}</template></el-table-column
            >
          </el-table>
        </template>
      </el-table-column>
      <el-table-column label="房型" min-width="260">
        <template #default="{ row }">
          <div class="room-type">
            <el-image :src="row.picUrl" fit="cover" />
            <div
              ><strong>{{ row.name }}</strong
              ><span>{{ row.introduction }}</span></div
            >
          </div>
        </template>
      </el-table-column>
      <el-table-column label="床型/面积" width="150"
        ><template #default="{ row }"
          >{{ row.bedType }} / {{ row.roomSize }}㎡</template
        ></el-table-column
      >
      <el-table-column prop="maxGuests" label="入住人数" width="90" />
      <el-table-column label="最低价" width="110"
        ><template #default="{ row }">¥{{ minPrice(row) }}</template></el-table-column
      >
      <el-table-column label="审核状态" width="110">
        <template #default="{ row }"
          ><el-tag :type="auditMeta(row.auditStatus).type">{{
            auditMeta(row.auditStatus).label
          }}</el-tag></template
        >
      </el-table-column>
      <el-table-column prop="auditRemark" label="审核意见" min-width="160" />
      <el-table-column label="操作" width="150" fixed="right">
        <template #default="{ row }">
          <template v-if="row.auditStatus === 1">
            <el-button
              v-hasPermi="['hotel:platform:room-type:audit']"
              link
              type="success"
              @click="openAudit(row, true)"
              >通过</el-button
            >
            <el-button
              v-hasPermi="['hotel:platform:room-type:audit']"
              link
              type="danger"
              @click="openAudit(row, false)"
              >驳回</el-button
            >
          </template>
          <span v-else>-</span>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <Dialog
    v-model="auditVisible"
    :title="auditForm.approved ? '通过房型终审' : '驳回房型终审'"
    width="520px"
  >
    <el-form label-width="90px">
      <el-form-item label="房型">{{ auditForm.name }}</el-form-item>
      <el-form-item label="审核意见" required>
        <el-input
          v-model="auditForm.remark"
          type="textarea"
          :rows="4"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="auditVisible = false">取消</el-button>
      <el-button
        :type="auditForm.approved ? 'success' : 'danger'"
        :loading="saving"
        @click="submitAudit"
        >确认{{ auditForm.approved ? '通过' : '驳回' }}</el-button
      >
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import {
  auditPlatformRoomType,
  getPlatformHotelOptions,
  getPlatformRoomTypes,
  type PlatformRoomTypeHotelOption
} from '@/api/hotel/roomTypeReview'
import type { HotelRoomType } from '@/api/merchant/hotel'

defineOptions({ name: 'HotelPlatformRoomTypeReview' })

const loading = ref(false)
const saving = ref(false)
const hotels = ref<PlatformRoomTypeHotelOption[]>([])
const shopId = ref<string | number>()
const roomTypes = ref<HotelRoomType[]>([])
const auditVisible = ref(false)
const auditForm = reactive({
  roomTypeId: '' as string | number,
  name: '',
  approved: true,
  remark: ''
})
const auditStatuses: Record<number, { label: string; type: string }> = {
  0: { label: '草稿', type: 'info' },
  1: { label: '审核中', type: 'warning' },
  2: { label: '审核通过', type: 'success' },
  3: { label: '审核驳回', type: 'danger' },
  4: { label: '已上架', type: 'success' },
  5: { label: '已下架', type: 'info' }
}
const auditMeta = (value: number) => auditStatuses[value] || auditStatuses[0]
const money = (value: number) => (Number(value || 0) / 100).toFixed(2)
const minPrice = (row: HotelRoomType) => {
  const values = (row.skus || []).map((item) => Number(item.price)).filter((value) => value > 0)
  return values.length ? money(Math.min(...values)) : '0.00'
}
const operationModeLabel = (value: string) =>
  ({
    SELF_OPERATED: '自营',
    PLATFORM_MANAGED: '托管',
    THIRD_PARTY: '第三方'
  })[value] || value

async function loadHotels() {
  hotels.value = (await getPlatformHotelOptions()) || []
  shopId.value = hotels.value[0]?.shopId
  if (shopId.value) await loadRoomTypes()
}

async function loadRoomTypes() {
  if (!shopId.value) return
  loading.value = true
  try {
    roomTypes.value = (await getPlatformRoomTypes(shopId.value)) || []
  } finally {
    loading.value = false
  }
}

function openAudit(row: HotelRoomType, approved: boolean) {
  Object.assign(auditForm, { roomTypeId: row.id, name: row.name, approved, remark: '' })
  auditVisible.value = true
}

async function submitAudit() {
  if (!auditForm.remark.trim()) return ElMessage.warning('请输入审核意见')
  saving.value = true
  try {
    await auditPlatformRoomType({
      shopId: shopId.value!,
      roomTypeId: auditForm.roomTypeId,
      approved: auditForm.approved,
      remark: auditForm.remark.trim()
    })
    ElMessage.success(auditForm.approved ? '房型终审已通过' : '房型已驳回')
    auditVisible.value = false
    await loadRoomTypes()
  } finally {
    saving.value = false
  }
}

onMounted(loadHotels)
</script>

<style scoped>
.sku-table {
  margin: 12px 48px;
  width: calc(100% - 96px);
}
.room-type {
  display: flex;
  align-items: center;
  gap: 10px;
}
.room-type .el-image {
  width: 64px;
  height: 48px;
  border-radius: 4px;
}
.room-type strong,
.room-type span {
  display: block;
}
.room-type span {
  max-width: 190px;
  margin-top: 4px;
  overflow: hidden;
  color: var(--el-text-color-secondary);
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
